import type {
  AllowanceLine,
  ClassificationStream,
  EmploymentTypeKey,
  PenaltyLine,
  RateContext,
  RateLine,
} from './award-rate-lookup';

export const MDM_AWARD_RATES_FLAG = 'MDM_AWARD_RATES_ENABLED';
export const MDM_AWARD_RATE_TTL_MS = 60_000;

const ENABLED_VALUES = new Set(['1', 'true', 'yes', 'on', 'enabled']);

export interface MdmAwardRatesEnv {
  [key: string]: string | undefined;
}

export interface MdmWorkerClassificationKeyInput {
  classificationStream?: string | null;
  classificationLevel?: string | null;
  payPoint?: string | null;
  employmentType?: string | null;
}

export interface MdmActiveRateRow {
  resolution_source: 'TENANT_OVERRIDE' | 'STATE_BASE' | 'FEDERAL_BASE' | string;
  award_rate_version_id: string | null;
  tenant_override_id: string | null;
  tenant_id: string | null;
  award_code: string;
  state: string | null;
  classification_stream: string;
  classification_level: string;
  pay_point: string | null;
  employment_type: string | null;
  ordinary_hourly_rate: string | number;
  effective_from: string;
  effective_to: string | null;
  source: string;
  idempotency_key: string | null;
  rate_payload: Record<string, unknown> | null;
}

export function isMdmAwardRatesEnabled(env: MdmAwardRatesEnv = process.env): boolean {
  const value = env[MDM_AWARD_RATES_FLAG];
  return value === undefined ? false : ENABLED_VALUES.has(value.trim().toLowerCase());
}

function clean(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export function buildMdmWorkerClassificationKeys(input: MdmWorkerClassificationKeyInput): string[] {
  const stream = clean(input.classificationStream)?.toLowerCase() ?? null;
  const level = clean(input.classificationLevel);
  const payPoint = clean(input.payPoint);
  const employmentType = clean(input.employmentType)?.toUpperCase() ?? null;

  if (!level) return [];

  const keys: string[] = [];
  if (stream) {
    if (payPoint && employmentType) keys.push(`${stream}:${level}:${payPoint}:${employmentType}`);
    if (payPoint) keys.push(`${stream}:${level}:${payPoint}`);
    keys.push(`${stream}:${level}`);
  }
  if (payPoint) {
    keys.push(`${level}.${payPoint}`);
    keys.push(`${level}:${payPoint}`);
  }
  keys.push(level);

  return Array.from(new Set(keys));
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function asEmploymentType(value: string | null): EmploymentTypeKey | null {
  if (value === 'PERMANENT' || value === 'CASUAL' || value === 'FIXED_TERM') return value;
  return null;
}

function sameClassification(rate: RateLine, mdmRate: MdmActiveRateRow): boolean {
  return (
    rate.classificationStream === (mdmRate.classification_stream as ClassificationStream) &&
    rate.classificationLevel === mdmRate.classification_level &&
    (rate.payPoint ?? '') === (mdmRate.pay_point ?? '')
  );
}

function upsertPenalty(
  penalties: PenaltyLine[],
  band: PenaltyLine['band'],
  employmentType: PenaltyLine['employmentType'],
  multiplier: number | null,
): PenaltyLine[] {
  if (multiplier === null) return penalties;
  const existing = penalties.findIndex(
    (p) => p.band === band && p.employmentType === employmentType,
  );
  const next: PenaltyLine = {
    band,
    employmentType,
    classificationStream: null,
    multiplier,
    description: 'MDM tenant award-rate override',
  };
  if (existing === -1) return [...penalties, next];
  return penalties.map((p, idx) => (idx === existing ? { ...p, multiplier } : p));
}

function upsertAllowance(
  allowances: AllowanceLine[],
  allowanceCode: string,
  amount: number | null,
  basis: AllowanceLine['basis'],
): AllowanceLine[] {
  if (amount === null) return allowances;
  const existing = allowances.findIndex((a) => a.allowanceCode === allowanceCode);
  const next: AllowanceLine = {
    allowanceCode,
    basis,
    amount,
    description: 'MDM tenant award-rate override',
  };
  if (existing === -1) return [...allowances, next];
  return allowances.map((a, idx) => (idx === existing ? { ...a, amount } : a));
}

function loadingPctToMultiplier(value: unknown): number | null {
  const pct = asNumber(value);
  return pct === null ? null : 1 + pct / 100;
}

function casualLoadingPctToMultiplier(
  loadingValue: unknown,
  casualLoadingValue: unknown,
): number | null {
  const loadingPct = asNumber(loadingValue);
  const casualLoadingPct = asNumber(casualLoadingValue);
  if (loadingPct === null || casualLoadingPct === null) return null;
  return 1 + (loadingPct + casualLoadingPct) / 100;
}

function overlayPayload(ctx: RateContext, payload: Record<string, unknown> | null): RateContext {
  if (!payload) return ctx;

  let penalties = ctx.penalties;
  penalties = upsertPenalty(
    penalties,
    'SATURDAY',
    'PERMANENT',
    loadingPctToMultiplier(payload.saturday_loading_pct),
  );
  penalties = upsertPenalty(
    penalties,
    'SUNDAY',
    'PERMANENT',
    loadingPctToMultiplier(payload.sunday_loading_pct),
  );
  penalties = upsertPenalty(
    penalties,
    'PUBLIC_HOLIDAY',
    'PERMANENT',
    loadingPctToMultiplier(payload.public_holiday_loading_pct),
  );
  penalties = upsertPenalty(
    penalties,
    'WEEKDAY_EVENING',
    'PERMANENT',
    loadingPctToMultiplier(payload.afternoon_loading_pct),
  );
  penalties = upsertPenalty(
    penalties,
    'WEEKDAY_AFTERNOON',
    'PERMANENT',
    loadingPctToMultiplier(payload.afternoon_loading_pct),
  );
  penalties = upsertPenalty(
    penalties,
    'WEEKDAY_NIGHT',
    'PERMANENT',
    loadingPctToMultiplier(payload.night_loading_pct),
  );

  penalties = upsertPenalty(
    penalties,
    'SATURDAY',
    'CASUAL',
    casualLoadingPctToMultiplier(payload.saturday_loading_pct, payload.casual_loading_pct),
  );
  penalties = upsertPenalty(
    penalties,
    'SUNDAY',
    'CASUAL',
    casualLoadingPctToMultiplier(payload.sunday_loading_pct, payload.casual_loading_pct),
  );
  penalties = upsertPenalty(
    penalties,
    'PUBLIC_HOLIDAY',
    'CASUAL',
    casualLoadingPctToMultiplier(payload.public_holiday_loading_pct, payload.casual_loading_pct),
  );
  penalties = upsertPenalty(
    penalties,
    'WEEKDAY_EVENING',
    'CASUAL',
    casualLoadingPctToMultiplier(payload.afternoon_loading_pct, payload.casual_loading_pct),
  );
  penalties = upsertPenalty(
    penalties,
    'WEEKDAY_AFTERNOON',
    'CASUAL',
    casualLoadingPctToMultiplier(payload.afternoon_loading_pct, payload.casual_loading_pct),
  );
  penalties = upsertPenalty(
    penalties,
    'WEEKDAY_NIGHT',
    'CASUAL',
    casualLoadingPctToMultiplier(payload.night_loading_pct, payload.casual_loading_pct),
  );

  let allowances = ctx.allowances;
  allowances = upsertAllowance(
    allowances,
    'SLEEPOVER',
    asNumber(payload.sleepover_allowance_amount),
    'PER_SHIFT',
  );
  allowances = upsertAllowance(allowances, 'KM', asNumber(payload.km_allowance_per_km), 'PER_KM');
  allowances = upsertAllowance(
    allowances,
    'BROKEN_SHIFT_1',
    asNumber(payload.broken_shift_allowance_amount),
    'PER_SHIFT',
  );

  return { ...ctx, penalties, allowances };
}

export function overlayMdmActiveRateOnContext(
  ctx: RateContext,
  mdmRate: MdmActiveRateRow,
): RateContext {
  const hourlyRate = asNumber(mdmRate.ordinary_hourly_rate);
  if (hourlyRate === null) return ctx;

  const existingIdx = ctx.rates.findIndex((r) => sameClassification(r, mdmRate));
  const nextRate: RateLine = {
    classificationStream: mdmRate.classification_stream as ClassificationStream,
    classificationLevel: mdmRate.classification_level,
    payPoint: mdmRate.pay_point,
    employmentType: asEmploymentType(mdmRate.employment_type),
    hourlyRate,
  };
  const rates =
    existingIdx === -1
      ? [...ctx.rates, nextRate]
      : ctx.rates.map((r, idx) => (idx === existingIdx ? { ...r, hourlyRate } : r));

  return overlayPayload(
    {
      ...ctx,
      awardCode: mdmRate.award_code,
      effectiveFrom: mdmRate.effective_from,
      effectiveTo: mdmRate.effective_to,
      resolutionSource: mdmRate.resolution_source as RateContext['resolutionSource'],
      tenantOverrideId: mdmRate.tenant_override_id,
      state: mdmRate.state,
      idempotencyKey: mdmRate.idempotency_key,
      rates,
    },
    mdmRate.rate_payload,
  );
}
